// UPost.com - Super Admin Dashboard
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Logo } from '../components/Logo';
import { OffSiteSEO } from '../components/OffSiteSEO';
import { KeywordTracker } from '../components/KeywordTracker';
import { VoiceAISearch } from '../components/VoiceAISearch';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import { AdminSupportManagement } from '../components/AdminSupportManagement';
import { ABTestDashboard } from './ABTestDashboard';
import { 
  Users, DollarSign, FileText, Sparkles, 
  TrendingUp, MessageSquare, Shield, ArrowLeft, Loader2, BookOpen, Target, Trash2, RefreshCw, Mail, CheckCircle, XCircle, Eye, MousePointer
} from 'lucide-react';

function ExchangeAdmin() {
  const [members, setMembers] = useState([]);
  const [books, setBooks] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState('members');
  const [loading, setLoading] = useState(true);
  const [creditEmail, setCreditEmail] = useState('');
  const [creditAmount, setCreditAmount] = useState(0);

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [m, b, c, r, s, rem] = await Promise.all([
        api.request('/club/admin/members'),
        api.request('/club/admin/books'),
        api.request('/club/admin/campaigns'),
        api.request('/club/admin/reviews'),
        api.request('/club/stats'),
        api.request('/club/admin/reminders').catch(() => ({ reminders: [] })),
      ]);
      setMembers(m.members || []);
      setBooks(b.books || []);
      setCampaigns(c.campaigns || []);
      setReviews(r.reviews || []);
      setStats(s);
      setReminders(rem.reminders || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function adjustCredits(email, amount, reason) {
    await api.request(`/club/admin/members/${email}/credits`, {
      method: 'PUT', data: { amount, reason }
    });
    loadData();
  }

  async function removeMember(email) {
    if (!window.confirm(`Remove ${email}?`)) return;
    await api.request(`/club/admin/members/${email}`, { method: 'DELETE' });
    loadData();
  }

  async function removeBook(id) {
    if (!window.confirm('Remove this book?')) return;
    await api.request(`/club/admin/books/${id}`, { method: 'DELETE' });
    loadData();
  }

  async function updateCampaignStatus(id, status) {
    await api.request(`/club/admin/campaigns/${id}/status`, {
      method: 'PUT', data: { status }
    });
    loadData();
  }

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Members', val: stats?.total_members || 0 },
          { label: 'Books', val: stats?.total_books || 0 },
          { label: 'Reviews', val: stats?.total_reviews || 0 },
          { label: 'Launches', val: stats?.active_launches || 0 },
          { label: 'Reminders', val: reminders.length },
        ].map(s => (
          <Card key={s.label}><CardContent className="p-4 text-center">
            <div className="text-2xl font-bold">{s.val}</div>
            <div className="text-xs text-gray-500">{s.label}</div>
          </CardContent></Card>
        ))}
      </div>

      <div className="flex gap-2 flex-wrap">
        {['members','books','campaigns','reviews','reminders'].map(t => (
          <Button key={t} size="sm" variant={tab === t ? 'default' : 'outline'}
            onClick={() => setTab(t)} className="capitalize">{t}</Button>
        ))}
        <Button size="sm" variant="ghost" onClick={loadData}><RefreshCw className="w-4 h-4" /></Button>
      </div>

      {tab === 'members' && (
        <Card><CardContent className="p-4">
          <div className="mb-4 flex gap-2 items-end">
            <div>
              <label className="text-xs text-gray-500">Email</label>
              <input className="border rounded px-2 py-1 text-sm w-48" value={creditEmail}
                onChange={e => setCreditEmail(e.target.value)} placeholder="member@email.com" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Credits (+/-)</label>
              <input className="border rounded px-2 py-1 text-sm w-24" type="number" value={creditAmount}
                onChange={e => setCreditAmount(Number(e.target.value))} />
            </div>
            <Button size="sm" onClick={() => { adjustCredits(creditEmail, creditAmount, 'admin'); setCreditEmail(''); setCreditAmount(0); }}
              disabled={!creditEmail}>Adjust</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-gray-500">
                <th className="py-2 pr-4">Name</th><th className="pr-4">Email</th><th className="pr-4">Credits</th>
                <th className="pr-4">Reviews</th><th className="pr-4">Joined</th><th></th>
              </tr></thead>
              <tbody>
                {members.map(m => (
                  <tr key={m.email} className="border-b hover:bg-gray-50">
                    <td className="py-2 pr-4 font-medium">{m.name}</td>
                    <td className="pr-4 text-gray-600">{m.email}</td>
                    <td className="pr-4">{m.credits}</td>
                    <td className="pr-4">{m.total_reviews || 0}</td>
                    <td className="pr-4 text-gray-500 text-xs">{m.joined_at?.slice(0, 10)}</td>
                    <td><Button size="sm" variant="ghost" className="text-red-500 h-7" onClick={() => removeMember(m.email)}>
                      <Trash2 className="w-3 h-3" /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent></Card>
      )}

      {tab === 'books' && (
        <Card><CardContent className="p-4">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-gray-500">
              <th className="py-2 pr-4">Title</th><th className="pr-4">Author</th><th className="pr-4">Genre</th>
              <th className="pr-4">Credits/Review</th><th className="pr-4">Reviews</th><th></th>
            </tr></thead>
            <tbody>
              {books.map(b => (
                <tr key={b.id} className="border-b hover:bg-gray-50">
                  <td className="py-2 pr-4 font-medium">{b.title}</td>
                  <td className="pr-4">{b.author}</td>
                  <td className="pr-4">{b.genre}</td>
                  <td className="pr-4">{b.credits_per_review}</td>
                  <td className="pr-4">{b.reviews_received || 0}</td>
                  <td><Button size="sm" variant="ghost" className="text-red-500 h-7" onClick={() => removeBook(b.id)}>
                    <Trash2 className="w-3 h-3" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent></Card>
      )}

      {tab === 'campaigns' && (
        <Card><CardContent className="p-4">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-gray-500">
              <th className="py-2 pr-4">Title</th><th className="pr-4">Author</th><th className="pr-4">Slots</th>
              <th className="pr-4">Claimed</th><th className="pr-4">Launch</th><th className="pr-4">Status</th><th></th>
            </tr></thead>
            <tbody>
              {campaigns.map(c => (
                <tr key={c.campaign_id} className="border-b hover:bg-gray-50">
                  <td className="py-2 pr-4 font-medium">{c.title}</td>
                  <td className="pr-4">{c.author_name}</td>
                  <td className="pr-4">{c.arc_slots}</td>
                  <td className="pr-4">{c.slots_claimed || 0}</td>
                  <td className="pr-4 text-xs">{c.launch_date}</td>
                  <td className="pr-4">
                    <select className="border rounded px-1 py-0.5 text-xs" value={c.status}
                      onChange={e => updateCampaignStatus(c.campaign_id, e.target.value)}>
                      <option value="accepting_readers">Accepting</option>
                      <option value="full">Full</option>
                      <option value="closed">Closed</option>
                      <option value="completed">Completed</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent></Card>
      )}

      {tab === 'reviews' && (
        <Card><CardContent className="p-4">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-gray-500">
              <th className="py-2 pr-4">Book</th><th className="pr-4">Reviewer</th><th className="pr-4">Credits</th>
              <th className="pr-4">Status</th><th className="pr-4">Date</th>
            </tr></thead>
            <tbody>
              {reviews.map(r => (
                <tr key={r.id} className="border-b hover:bg-gray-50">
                  <td className="py-2 pr-4 font-medium">{r.book_title}</td>
                  <td className="pr-4">{r.reviewer_name}</td>
                  <td className="pr-4">{r.credits_awarded}</td>
                  <td className="pr-4"><span className={`px-2 py-0.5 rounded text-xs ${r.status === 'verified' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{r.status}</span></td>
                  <td className="pr-4 text-xs text-gray-500">{r.submitted_at?.slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent></Card>
      )}

      {tab === 'reminders' && (
        <Card><CardContent className="p-4">
          {reminders.length === 0 ? (
            <p className="text-gray-500 text-sm">No launch day reminders queued yet. Reminders auto-send when an ARC campaign's launch date arrives.</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-gray-500">
                <th className="py-2 pr-4">Book</th><th className="pr-4">Reader</th><th className="pr-4">Email</th><th className="pr-4">Sent</th>
              </tr></thead>
              <tbody>
                {reminders.map((r, i) => (
                  <tr key={i} className="border-b hover:bg-gray-50">
                    <td className="py-2 pr-4 font-medium">{r.book_title}</td>
                    <td className="pr-4">{r.reader_name}</td>
                    <td className="pr-4 text-gray-600">{r.reader_email}</td>
                    <td className="pr-4 text-xs text-gray-500">{r.sent_at?.slice(0, 16)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent></Card>
      )}
    </div>
  );
}

function CleanupAdmin() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  async function runCleanup() {
    if (!window.confirm('This will permanently delete all test data (TEST_ prefixed entries). Continue?')) return;
    setLoading(true);
    try {
      const res = await api.request('/club/admin/cleanup-test-data', { method: 'POST' });
      setResult(res);
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle>Test Data Cleanup</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600 mb-4">
            Remove all test/dummy data from the database before going live. This deletes entries with TEST_ prefixes and test@ emails from Exchange members, books, reviews, campaigns, claims, shares, and affiliates.
          </p>
          <Button onClick={runCleanup} disabled={loading} variant="destructive">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cleaning...</> : <><Trash2 className="w-4 h-4 mr-2" /> Run Cleanup</>}
          </Button>
          {result && (
            <div className="mt-4 bg-green-50 border border-green-200 rounded p-4">
              <p className="font-medium text-green-800 mb-2">Cleanup Complete — {result.total_removed} items removed</p>
              <div className="grid grid-cols-2 gap-2 text-sm text-green-700">
                {Object.entries(result.details || {}).map(([k, v]) => (
                  <div key={k}>{k.replace(/_/g, ' ')}: <strong>{v}</strong></div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function EmailLogAdmin() {
  const [emails, setEmails] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => { loadEmails(); }, []);

  async function loadEmails() {
    setLoading(true);
    try {
      const res = await api.request('/emails/admin/log?limit=200');
      setEmails(res.emails || []);
      setStats(res.stats || {});
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  const filtered = filter === 'all' ? emails : emails.filter(e => {
    if (filter === 'delivered') return e.delivered;
    if (filter === 'opened') return e.opened;
    if (filter === 'bounced') return e.bounced;
    if (filter === 'failed') return e.status === 'error';
    return true;
  });

  const statusBadge = (e) => {
    if (e.bounced) return <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-700">Bounced ({e.bounce_type})</span>;
    if (e.status === 'error') return <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-700">Failed</span>;
    if (e.spam) return <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-700">Spam</span>;
    if (e.opened) return <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-700">Opened</span>;
    if (e.delivered) return <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700">Delivered</span>;
    if (e.status === 'sent') return <span className="px-2 py-0.5 rounded text-xs bg-yellow-100 text-yellow-700">Sent</span>;
    return <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">{e.status}</span>;
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {[
            { label: 'Total Sent', val: stats.total_sent, icon: Mail, color: 'text-slate-600' },
            { label: 'Delivered', val: stats.delivered, icon: CheckCircle, color: 'text-blue-600' },
            { label: 'Opened', val: stats.opened, icon: Eye, color: 'text-green-600' },
            { label: 'Clicked', val: stats.clicked, icon: MousePointer, color: 'text-purple-600' },
            { label: 'Bounced', val: stats.bounced, icon: XCircle, color: 'text-red-600' },
            { label: 'Failed', val: stats.failed, icon: XCircle, color: 'text-orange-600' },
          ].map(s => (
            <Card key={s.label}><CardContent className="p-4 text-center">
              <s.icon className={`w-5 h-5 mx-auto mb-1 ${s.color}`} />
              <div className="text-2xl font-bold">{s.val}</div>
              <div className="text-xs text-gray-500">{s.label}</div>
            </CardContent></Card>
          ))}
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {['all','delivered','opened','bounced','failed'].map(f => (
          <Button key={f} size="sm" variant={filter === f ? 'default' : 'outline'}
            onClick={() => setFilter(f)} className="capitalize">{f}</Button>
        ))}
        <Button size="sm" variant="ghost" onClick={loadEmails}><RefreshCw className="w-4 h-4" /></Button>
      </div>

      <Card><CardContent className="p-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-gray-500">
              <th className="py-2 pr-3">To</th>
              <th className="pr-3">Subject</th>
              <th className="pr-3">Type</th>
              <th className="pr-3">Status</th>
              <th className="pr-3">Opened</th>
              <th className="pr-3">Clicked</th>
              <th className="pr-3">Sent</th>
            </tr></thead>
            <tbody>
              {filtered.map((e, i) => (
                <tr key={e.id || i} className="border-b hover:bg-gray-50">
                  <td className="py-2 pr-3 font-medium text-gray-900 max-w-[180px] truncate">{e.to}</td>
                  <td className="pr-3 text-gray-700 max-w-[250px] truncate">{e.subject}</td>
                  <td className="pr-3"><span className="px-1.5 py-0.5 rounded text-xs bg-slate-100 text-slate-600">{e.email_type}</span></td>
                  <td className="pr-3">{statusBadge(e)}</td>
                  <td className="pr-3 text-center">{e.opened ? <Eye className="w-4 h-4 text-green-500 inline" /> : <span className="text-gray-300">—</span>}</td>
                  <td className="pr-3 text-center">{e.clicked ? <MousePointer className="w-4 h-4 text-purple-500 inline" /> : <span className="text-gray-300">—</span>}</td>
                  <td className="pr-3 text-xs text-gray-500 whitespace-nowrap">{e.sent_at?.slice(0, 16).replace('T', ' ')}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan="7" className="py-8 text-center text-gray-400">No emails found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      <p className="text-xs text-gray-400">
        Tracking updates automatically via SMTP2GO webhooks. Configure webhook at SMTP2GO dashboard pointing to: https://upost.com/api/emails/webhook/smtp2go
      </p>
    </div>
  );
}


export function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [support, setSupport] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogSettings, setBlogSettings] = useState(null);
  const [keywords, setKeywords] = useState([]);
  const [keywordStats, setKeywordStats] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [topReferrers, setTopReferrers] = useState([]);
  const [collaborationStats, setCollaborationStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [keywordFilter, setKeywordFilter] = useState('all');
  const [blogGenerating, setBlogGenerating] = useState(false);
  const [keywordActionLoading, setKeywordActionLoading] = useState(null);
  const [editingBlog, setEditingBlog] = useState(null);
  const [blogEditForm, setBlogEditForm] = useState({ title: '', content: '', excerpt: '', status: 'published' });
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    checkAdminAccess();
    loadDashboardData();
  }, []);

  async function checkAdminAccess() {
    try {
      await api.request('/admin/ai/override');
    } catch (err) {
      // Not an admin, redirect
      navigate('/dashboard');
    }
  }

  async function handleUpdateTier(userId, newTier) {
    if (!confirm(`Update user to ${newTier.toUpperCase()} tier?`)) return;
    
    try {
      await api.request('/admin/users/update-tier', {
        method: 'POST',
        body: JSON.stringify({ user_id: userId, new_tier: newTier })
      });
      
      // Reload users data
      const usersData = await api.request('/admin/users');
      setUsers(usersData.users);
      alert('User tier updated successfully!');
    } catch (err) {
      alert('Failed to update user tier: ' + err.message);
    }
  }

  async function handleDeleteUser(userId, userEmail) {
    if (!confirm(`Are you sure you want to DELETE user ${userEmail}?\n\nThis will permanently delete:\n- Their account\n- All their documents\n- All collaborations\n- All invitations\n\nThis action CANNOT be undone!`)) {
      return;
    }
    
    // Double confirmation for safety
    const confirmText = prompt(`Type "DELETE" to confirm deletion of ${userEmail}:`);
    if (confirmText !== 'DELETE') {
      alert('Deletion cancelled');
      return;
    }
    
    try {
      const result = await api.request(`/admin/users/${userId}`, {
        method: 'DELETE'
      });
      
      // Reload users data
      const usersData = await api.request('/admin/users');
      setUsers(usersData.users);
      
      alert(`User deleted successfully!\nDeleted: ${result.deleted.documents} documents, ${result.deleted.collaborations} collaborations`);
    } catch (err) {
      alert('Failed to delete user: ' + err.message);
    }
  }

  async function loadDashboardData() {
    try {
      const [statsData, usersData, paymentsData, supportData, suggestionsData, blogData, blogSettingsData, keywordsData, referralStatsData, topReferrersData, collabStatsData] = await Promise.all([
        api.request('/admin/dashboard/stats'),
        api.request('/admin/users?limit=50'),
        api.request('/admin/payments?limit=10'),
        api.request('/admin/support?limit=10'),
        api.request('/suggestions/list'),
        api.request('/blog/posts?limit=50'),
        api.request('/blog/admin/settings'),
        api.request('/keywords/admin/keywords'),
        api.request('/admin/referrals/stats'),
        api.request('/admin/referrals/top'),
        api.request('/admin/collaboration/stats').catch(() => null)
      ]);

      setStats(statsData);
      setUsers(usersData.users);
      setPayments(paymentsData.payments);
      setSupport(supportData.requests);
      setSuggestions(suggestionsData.suggestions || []);
      setBlogPosts(blogData.posts || []);
      setBlogSettings(blogSettingsData || {});
      setKeywords(keywordsData.keywords || []);
      setKeywordStats(keywordsData.stats || {});
      setReferralStats(referralStatsData);
      setTopReferrers(topReferrersData.top_referrers || []);
      setCollaborationStats(collabStatsData);
    } catch (err) {
      console.error('Failed to load admin data:', err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white">
        <div className="container mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Shield size={32} />
              <div>
                <h1 className="text-2xl font-bold">Super Admin Dashboard</h1>
                <p className="text-purple-100">Platform Analytics & Management</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm">{user?.email}</span>
              <Link to="/dashboard">
                <Button variant="ghost" className="text-white hover:bg-white/20">
                  <ArrowLeft size={16} className="mr-2" />
                  User View
                </Button>
              </Link>
              <Button variant="ghost" onClick={logout} className="text-white hover:bg-white/20">
                Log Out
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Users</p>
                  <p className="text-3xl font-bold">{stats?.users?.total || 0}</p>
                  <p className="text-xs text-gray-500">
                    {stats?.users?.new_last_30_days || 0} new this month
                  </p>
                </div>
                <Users className="text-purple-600" size={40} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Pro Subscriptions</p>
                  <p className="text-3xl font-bold">{stats?.users?.pro || 0}</p>
                  <p className="text-xs text-gray-500">
                    {stats?.subscriptions?.conversion_rate || 0}% conversion rate
                  </p>
                </div>
                <TrendingUp className="text-green-600" size={40} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Monthly Revenue (MRR)</p>
                  <p className="text-3xl font-bold">${stats?.subscriptions?.mrr?.toFixed(2) || 0}</p>
                  <p className="text-xs text-gray-500">
                    ${stats?.revenue?.total?.toFixed(2) || 0} lifetime
                  </p>
                </div>
                <DollarSign className="text-blue-600" size={40} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">AI Words Generated</p>
                  <p className="text-3xl font-bold">
                    {(stats?.ai?.total_words_generated || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500">
                    {stats?.documents?.total || 0} documents
                  </p>
                </div>
                <Sparkles className="text-yellow-600" size={40} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Subscription Metrics Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Free Tier Users</p>
                  <p className="text-2xl font-bold">{stats?.users?.free || 0}</p>
                  <p className="text-xs text-gray-500">
                    {((stats?.users?.free / stats?.users?.total) * 100 || 0).toFixed(1)}% of total
                  </p>
                </div>
                <Users className="text-gray-500" size={32} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Referrals</p>
                  <p className="text-2xl font-bold">{stats?.referrals?.total_referrals || 0}</p>
                  <p className="text-xs text-gray-500">
                    {stats?.referrals?.pro_referrals || 0} Pro referrals
                  </p>
                </div>
                <Users className="text-purple-600" size={32} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Transactions</p>
                  <p className="text-2xl font-bold">{stats?.revenue?.total_transactions || 0}</p>
                  <p className="text-xs text-gray-500">
                    {stats?.revenue?.monthly_transactions || 0} this month
                  </p>
                </div>
                <FileText className="text-indigo-600" size={32} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Commission Liability</p>
                  <p className="text-2xl font-bold">
                    ${stats?.referrals?.monthly_commission_liability?.toFixed(2) || '0.00'}
                  </p>
                  <p className="text-xs text-gray-500">
                    Monthly payout
                  </p>
                </div>
                <DollarSign className="text-orange-600" size={32} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="overflow-x-auto mb-6">
            <TabsList className="inline-flex min-w-full w-max">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="analytics">Analytics</TabsTrigger>
              <TabsTrigger value="collaboration">Collaboration</TabsTrigger>
              <TabsTrigger value="abtesting">A/B Testing</TabsTrigger>
              <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
              <TabsTrigger value="referrals">Referrals</TabsTrigger>
              <TabsTrigger value="users">Users</TabsTrigger>
              <TabsTrigger value="payments">Payments</TabsTrigger>
              <TabsTrigger value="support">Support</TabsTrigger>
              <TabsTrigger value="suggestions">Suggestions</TabsTrigger>
              <TabsTrigger value="blog">Blog</TabsTrigger>
              <TabsTrigger value="keywords">Keywords</TabsTrigger>
              <TabsTrigger value="rankings">Keyword Rankings</TabsTrigger>
              <TabsTrigger value="offsiteseo">Off-Site SEO</TabsTrigger>
              <TabsTrigger value="voice-ai">Voice & AI Search</TabsTrigger>
              <TabsTrigger value="exchange">Exchange</TabsTrigger>
              <TabsTrigger value="email-log">Emails</TabsTrigger>
              <TabsTrigger value="cleanup">Cleanup</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="overview">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Recent Users</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {users.slice(0, 5).map(u => (
                      <div key={u.id} className="flex justify-between items-center border-b pb-2">
                        <div>
                          <p className="font-medium">{u.full_name}</p>
                          <p className="text-sm text-gray-600">{u.email}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${
                          u.subscription_tier === 'pro' 
                            ? 'bg-purple-100 text-purple-700' 
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {u.subscription_tier?.toUpperCase()}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Recent Payments</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {payments.filter(p => p.payment_status === 'completed').slice(0, 5).map(p => (
                      <div key={p.id} className="flex justify-between items-center border-b pb-2">
                        <div>
                          <p className="font-medium">${p.amount}</p>
                          <p className="text-sm text-gray-600">{p.metadata?.package_name || 'Package'}</p>
                        </div>
                        <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700">
                          completed
                        </span>
                      </div>
                    ))}
                    {payments.filter(p => p.payment_status === 'completed').length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-4">No completed payments yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="analytics">
            <AnalyticsDashboard />
          </TabsContent>

          <TabsContent value="collaboration">
            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-500">Total Collaborations</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {collaborationStats?.summary?.total_collaborations || 0}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-500">Active</p>
                    <p className="text-2xl font-bold text-green-600">
                      {collaborationStats?.summary?.active_collaborations || 0}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-500">Pending Invites</p>
                    <p className="text-2xl font-bold text-yellow-600">
                      {collaborationStats?.summary?.pending_invites || 0}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-500">Documents Shared</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {collaborationStats?.summary?.documents_shared || 0}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-500">Acceptance Rate</p>
                    <p className="text-2xl font-bold text-indigo-600">
                      {collaborationStats?.summary?.acceptance_rate || 0}%
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Growth Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Collaboration Growth
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-sm text-green-700">Last 7 Days</p>
                      <p className="text-3xl font-bold text-green-600">
                        +{collaborationStats?.growth?.new_last_7_days || 0}
                      </p>
                      <p className="text-xs text-green-600">new collaborations</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-sm text-blue-700">Last 30 Days</p>
                      <p className="text-3xl font-bold text-blue-600">
                        +{collaborationStats?.growth?.new_last_30_days || 0}
                      </p>
                      <p className="text-xs text-blue-600">new collaborations</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Top Sharers */}
                <Card>
                  <CardHeader>
                    <CardTitle>Top Document Sharers</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {collaborationStats?.top_sharers?.length > 0 ? (
                      <div className="space-y-3">
                        {collaborationStats.top_sharers.map((sharer, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <div>
                              <p className="font-medium text-sm">{sharer.email}</p>
                              <p className="text-xs text-gray-500">{sharer.unique_docs_count} documents shared</p>
                            </div>
                            <span className="text-purple-600 font-bold">{sharer.shared_count}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">No collaboration data yet</p>
                    )}
                  </CardContent>
                </Card>

                {/* Top Collaborators */}
                <Card>
                  <CardHeader>
                    <CardTitle>Most Active Collaborators</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {collaborationStats?.top_collaborators?.length > 0 ? (
                      <div className="space-y-3">
                        {collaborationStats.top_collaborators.map((collab, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <div>
                              <p className="font-medium text-sm">{collab.email}</p>
                              <p className="text-xs text-gray-500">{collab.documents_count} documents</p>
                            </div>
                            <span className="text-green-600 font-bold">{collab.collab_count}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">No collaboration data yet</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Role Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle>Role Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-6">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-blue-500 rounded"></div>
                      <span>Editors: {collaborationStats?.role_distribution?.editor || 0}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-gray-400 rounded"></div>
                      <span>Viewers: {collaborationStats?.role_distribution?.viewer || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Recent Activity */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent Collaboration Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  {collaborationStats?.recent_activity?.length > 0 ? (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {collaborationStats.recent_activity.slice(0, 15).map((activity, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                          <div>
                            <p className="font-medium text-sm">{activity.user_email}</p>
                            <p className="text-xs text-gray-500">
                              Invited by {activity.owner_email} as {activity.role}
                            </p>
                          </div>
                          <div className="text-right">
                            <span className={`text-xs px-2 py-1 rounded ${
                              activity.status === 'active' ? 'bg-green-100 text-green-700' :
                              activity.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {activity.status}
                            </span>
                            <p className="text-xs text-gray-400 mt-1">
                              {activity.created_at ? new Date(activity.created_at).toLocaleDateString() : 'N/A'}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No recent activity</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="abtesting">
            <ABTestDashboard />
          </TabsContent>

          <TabsContent value="subscriptions">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <Card>
                <CardHeader>
                  <CardTitle>Subscription Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-4 bg-purple-50 rounded-lg">
                      <div>
                        <p className="font-semibold text-purple-900">Pro Subscribers</p>
                        <p className="text-sm text-purple-700">$19/month per user</p>
                      </div>
                      <p className="text-3xl font-bold text-purple-600">{stats?.users?.pro || 0}</p>
                    </div>
                    
                    <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-semibold text-gray-900">Free Tier Users</p>
                        <p className="text-sm text-gray-700">18,400 AI words/month</p>
                      </div>
                      <p className="text-3xl font-bold text-gray-600">{stats?.users?.free || 0}</p>
                    </div>

                    <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-900 font-medium mb-2">Conversion Metrics</p>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-blue-700">Conversion Rate:</span>
                          <span className="font-semibold text-blue-900">{stats?.subscriptions?.conversion_rate || 0}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-blue-700">MRR (Monthly Recurring):</span>
                          <span className="font-semibold text-blue-900">${stats?.subscriptions?.mrr?.toFixed(2) || '0.00'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-blue-700">ARR (Annual Recurring):</span>
                          <span className="font-semibold text-blue-900">${((stats?.subscriptions?.mrr || 0) * 12).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Revenue Insights</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-4 bg-green-50 rounded-lg">
                      <p className="text-sm text-green-700 mb-1">Total Revenue</p>
                      <p className="text-3xl font-bold text-green-600">
                        ${stats?.revenue?.total?.toFixed(2) || '0.00'}
                      </p>
                      <p className="text-xs text-green-600 mt-1">
                        All-time earnings from {stats?.revenue?.total_transactions || 0} transactions
                      </p>
                    </div>

                    <div className="p-4 bg-emerald-50 rounded-lg">
                      <p className="text-sm text-emerald-700 mb-1">Last 30 Days</p>
                      <p className="text-3xl font-bold text-emerald-600">
                        ${stats?.revenue?.last_30_days?.toFixed(2) || '0.00'}
                      </p>
                      <p className="text-xs text-emerald-600 mt-1">
                        From {stats?.revenue?.monthly_transactions || 0} transactions this month
                      </p>
                    </div>

                    <div className="p-4 bg-amber-50 rounded-lg">
                      <p className="text-sm text-amber-700 mb-1">Average Customer LTV</p>
                      <p className="text-3xl font-bold text-amber-600">
                        ${stats?.subscriptions?.lifetime_value?.toFixed(2) || '0.00'}
                      </p>
                      <p className="text-xs text-amber-600 mt-1">
                        Lifetime value per user
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Pro Subscribers</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b">
                      <tr className="text-left">
                        <th className="pb-2">Name</th>
                        <th className="pb-2">Email</th>
                        <th className="pb-2">Status</th>
                        <th className="pb-2">AI Words Used</th>
                        <th className="pb-2">Subscribed Since</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.filter(u => u.subscription_tier === 'pro').map(u => (
                        <tr key={u.id} className="border-b">
                          <td className="py-3">{u.full_name}</td>
                          <td className="py-3">{u.email}</td>
                          <td className="py-3">
                            <span className="text-xs px-2 py-1 rounded bg-purple-100 text-purple-700">
                              ACTIVE PRO
                            </span>
                          </td>
                          <td className="py-3">{u.ai_words_used_this_month?.toLocaleString() || 0}</td>
                          <td className="py-3 text-sm text-gray-600">
                            {new Date(u.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                      {users.filter(u => u.subscription_tier === 'pro').length === 0 && (
                        <tr>
                          <td colSpan="5" className="py-8 text-center text-gray-500">
                            No Pro subscribers yet. Share upgrade benefits to convert free users!
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="referrals">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <Card>
                <CardHeader>
                  <CardTitle>Referral Program Overview</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-4 bg-purple-50 rounded-lg">
                      <div>
                        <p className="font-semibold text-purple-900">Total Referrals</p>
                        <p className="text-sm text-purple-700">All signups via referral codes</p>
                      </div>
                      <p className="text-3xl font-bold text-purple-600">
                        {referralStats?.referrals?.total || 0}
                      </p>
                    </div>
                    
                    <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
                      <div>
                        <p className="font-semibold text-green-900">Pro Referrals</p>
                        <p className="text-sm text-green-700">Paying customers from referrals</p>
                      </div>
                      <p className="text-3xl font-bold text-green-600">
                        {referralStats?.referrals?.pro || 0}
                      </p>
                    </div>

                    <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-semibold text-gray-900">Free Referrals</p>
                        <p className="text-sm text-gray-700">Free tier signups</p>
                      </div>
                      <p className="text-3xl font-bold text-gray-600">
                        {referralStats?.referrals?.free || 0}
                      </p>
                    </div>

                    <div className="flex justify-between items-center p-4 bg-red-50 rounded-lg">
                      <div>
                        <p className="font-semibold text-red-900">Flagged for Fraud</p>
                        <p className="text-sm text-red-700">Suspicious activity detected</p>
                      </div>
                      <p className="text-3xl font-bold text-red-600">
                        {referralStats?.referrals?.flagged || 0}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Commission Tracking</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-4 bg-green-50 rounded-lg">
                      <p className="text-sm text-green-700 mb-1">Total Paid Out</p>
                      <p className="text-3xl font-bold text-green-600">
                        ${referralStats?.commissions?.paid_out?.toFixed(2) || '0.00'}
                      </p>
                      <p className="text-xs text-green-600 mt-1">Lifetime affiliate payouts</p>
                    </div>

                    <div className="p-4 bg-orange-50 rounded-lg">
                      <p className="text-sm text-orange-700 mb-1">Monthly Liability</p>
                      <p className="text-3xl font-bold text-orange-600">
                        ${referralStats?.commissions?.monthly_liability?.toFixed(2) || '0.00'}
                      </p>
                      <p className="text-xs text-orange-600 mt-1">
                        {referralStats?.referrals?.pro || 0} Pro referrals × $4.75/month
                      </p>
                    </div>

                    <div className="p-4 bg-yellow-50 rounded-lg">
                      <p className="text-sm text-yellow-700 mb-1">Pending Commissions</p>
                      <p className="text-3xl font-bold text-yellow-600">
                        ${referralStats?.commissions?.pending?.toFixed(2) || '0.00'}
                      </p>
                      <p className="text-xs text-yellow-600 mt-1">Awaiting payment clearance</p>
                    </div>

                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-700 mb-1">Tax Compliance</p>
                      <p className="text-3xl font-bold text-blue-600">
                        {referralStats?.compliance?.users_with_w9 || 0}
                      </p>
                      <p className="text-xs text-blue-600 mt-1">Users with W-9 on file</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Top Referrers</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b">
                      <tr className="text-left">
                        <th className="pb-2">Name</th>
                        <th className="pb-2">Email</th>
                        <th className="pb-2">Referral Code</th>
                        <th className="pb-2">Total Refs</th>
                        <th className="pb-2">Pro Refs</th>
                        <th className="pb-2">Commissions</th>
                        <th className="pb-2">W-9</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topReferrers.map(ref => (
                        <tr key={ref.user_id} className="border-b">
                          <td className="py-3">{ref.name}</td>
                          <td className="py-3">{ref.email}</td>
                          <td className="py-3 font-mono text-sm bg-gray-100 px-2 rounded">
                            {ref.referral_code}
                          </td>
                          <td className="py-3">{ref.total_referrals}</td>
                          <td className="py-3">
                            <span className="text-xs px-2 py-1 rounded bg-purple-100 text-purple-700">
                              {ref.pro_referrals}
                            </span>
                          </td>
                          <td className="py-3 font-semibold text-green-600">
                            ${ref.total_commissions?.toFixed(2) || '0.00'}
                          </td>
                          <td className="py-3">
                            {ref.w9_on_file ? (
                              <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700">
                                ✓ Filed
                              </span>
                            ) : (
                              <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-700">
                                Missing
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {topReferrers.length === 0 && (
                        <tr>
                          <td colSpan="7" className="py-8 text-center text-gray-500">
                            No referrers yet. Referral program will populate as users share their codes.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>All Users ({users.length})</span>
                  <div className="flex gap-2">
                    <select 
                      className="text-sm border rounded px-3 py-1"
                      onChange={(e) => {
                        const filtered = e.target.value === 'all' 
                          ? users 
                          : users.filter(u => u.subscription_tier === e.target.value);
                        // For now, just keeping all users visible
                      }}
                    >
                      <option value="all">All Tiers</option>
                      <option value="free">Free Only</option>
                      <option value="pro">Pro Only</option>
                    </select>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b">
                      <tr className="text-left text-sm">
                        <th className="pb-2">Name</th>
                        <th className="pb-2">Email</th>
                        <th className="pb-2">Tier</th>
                        <th className="pb-2">AI Words</th>
                        <th className="pb-2">Created</th>
                        <th className="pb-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(u => (
                        <tr key={u.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 font-medium">{u.full_name}</td>
                          <td className="py-3 text-sm text-gray-700">{u.email}</td>
                          <td className="py-3">
                            <select
                              value={u.subscription_tier || 'free'}
                              onChange={(e) => handleUpdateTier(u.id, e.target.value)}
                              className="text-xs px-2 py-1 rounded border font-medium"
                            >
                              <option value="free">FREE</option>
                              <option value="pro">PRO</option>
                              <option value="team">TEAM</option>
                              <option value="admin">ADMIN</option>
                            </select>
                          </td>
                          <td className="py-3 text-sm">
                            {u.ai_words_used_this_month?.toLocaleString() || 0}
                          </td>
                          <td className="py-3 text-sm text-gray-600">
                            {new Date(u.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-3 text-right">
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeleteUser(u.id, u.email)}
                              disabled={u.is_super_admin}
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {users.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No users found
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="payments">
            <Card>
              <CardHeader>
                <CardTitle>Payment Transactions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b">
                      <tr className="text-left">
                        <th className="pb-2">Date</th>
                        <th className="pb-2">Package</th>
                        <th className="pb-2">Amount</th>
                        <th className="pb-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map(p => (
                        <tr key={p.id} className="border-b">
                          <td className="py-3 text-sm">
                            {new Date(p.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-3">{p.metadata?.package_name || p.package_id}</td>
                          <td className="py-3 font-medium">${p.amount}</td>
                          <td className="py-3">
                            <span className={`text-xs px-2 py-1 rounded ${
                              p.payment_status === 'completed' 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {p.payment_status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="support">
            <AdminSupportManagement />
          </TabsContent>

          {/* Suggestions Tab */}
          <TabsContent value="suggestions">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="text-blue-600" />
                  User Suggestions
                </CardTitle>
              </CardHeader>
              <CardContent>
                {suggestions.length === 0 ? (
                  <p className="text-center text-gray-600 py-8">No suggestions yet</p>
                ) : (
                  <div className="space-y-4">
                    {suggestions.map(suggestion => {
                      const categoryColors = {
                        feature: 'bg-blue-100 text-blue-700',
                        bug: 'bg-red-100 text-red-700',
                        improvement: 'bg-purple-100 text-purple-700',
                        content: 'bg-green-100 text-green-700',
                        other: 'bg-gray-100 text-gray-700'
                      };
                      const statusColors = {
                        new: 'bg-yellow-100 text-yellow-700',
                        reviewed: 'bg-blue-100 text-blue-700',
                        planned: 'bg-purple-100 text-purple-700',
                        implemented: 'bg-green-100 text-green-700',
                        declined: 'bg-gray-100 text-gray-700'
                      };
                      const categoryIcons = {
                        feature: '✨',
                        bug: '🐛',
                        improvement: '📈',
                        content: '📝',
                        other: '💡'
                      };

                      return (
                        <div key={suggestion.id} className="border-2 border-gray-200 rounded-lg p-4 hover:border-purple-300 transition-colors">
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className={`text-xs px-2 py-1 rounded font-semibold ${categoryColors[suggestion.category] || categoryColors.other}`}>
                                  {categoryIcons[suggestion.category] || '💡'} {suggestion.category?.toUpperCase()}
                                </span>
                                <span className={`text-xs px-2 py-1 rounded font-semibold ${statusColors[suggestion.status] || statusColors.new}`}>
                                  {suggestion.status?.toUpperCase() || 'NEW'}
                                </span>
                              </div>
                              <p className="font-medium text-gray-900">{suggestion.user_email}</p>
                              <p className="text-xs text-gray-500">
                                {new Date(suggestion.created_at).toLocaleString()}
                              </p>
                            </div>
                          </div>
                          
                          <div className="bg-gray-50 rounded-lg p-3 mb-3">
                            <p className="text-sm text-gray-800 whitespace-pre-wrap">{suggestion.suggestion}</p>
                          </div>

                          <div className="flex gap-2">
                            <select
                              className="text-sm px-3 py-1 border border-gray-300 rounded"
                              value={suggestion.status || 'new'}
                              onChange={async (e) => {
                                try {
                                  await api.request(`/suggestions/${suggestion.id}/status`, {
                                    method: 'PUT',
                                    body: JSON.stringify({ status: e.target.value })
                                  });
                                  // Reload suggestions
                                  const suggestionsData = await api.request('/suggestions/list');
                                  setSuggestions(suggestionsData.suggestions || []);
                                  alert('Status updated successfully!');
                                } catch (err) {
                                  alert('Failed to update status: ' + err.message);
                                }
                              }}
                            >
                              <option value="new">New</option>
                              <option value="reviewed">Reviewed</option>
                              <option value="planned">Planned</option>
                              <option value="implemented">Implemented</option>
                              <option value="declined">Declined</option>
                            </select>
                            <a
                              href={`mailto:${suggestion.user_email}?subject=Re: Your UPost Suggestion`}
                              className="text-sm px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                              📧 Reply
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Blog Tab */}
          <TabsContent value="blog">
            <div className="space-y-6">
              {/* Blog Settings */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="text-purple-600" />
                    Auto-Blog Settings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                      <div>
                        <p className="font-semibold text-purple-900">Auto-Generation</p>
                        <p className="text-sm text-gray-600">
                          Status: {blogSettings?.enabled ? '✅ Enabled' : '❌ Disabled'}
                        </p>
                        <p className="text-sm text-gray-600">
                          Frequency: Every {blogSettings?.frequency_hours || 72} hours
                        </p>
                        {blogSettings?.last_generation && (
                          <p className="text-xs text-gray-500 mt-1">
                            Last: {new Date(blogSettings.last_generation).toLocaleString()}
                          </p>
                        )}
                        {blogSettings?.next_generation && (
                          <p className="text-xs text-gray-500">
                            Next: {new Date(blogSettings.next_generation).toLocaleString()}
                          </p>
                        )}
                      </div>
                      <Button
                        onClick={async () => {
                          if (!confirm('Generate a new AI blog post? This will take 1-2 minutes and will be immediately published to your blog.')) return;
                          setBlogGenerating(true);
                          try {
                            const response = await api.request('/blog/generate-auto?force=true', { method: 'POST' });
                            alert(`✅ Blog post generated and published!\n\nTitle: ${response.blog_post.title}\n\nSearch engines have been notified. Check your /blog page!`);
                            loadDashboardData();
                          } catch (err) {
                            alert('❌ Failed to generate blog post: ' + err.message);
                          } finally {
                            setBlogGenerating(false);
                          }
                        }}
                        className="bg-purple-600 hover:bg-purple-700"
                        disabled={blogGenerating}
                      >
                        {blogGenerating ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin inline" />
                            Generating...
                          </>
                        ) : (
                          '🤖 Generate Now'
                        )}
                      </Button>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="p-4 border rounded-lg">
                        <p className="font-semibold mb-2">Topics ({blogSettings?.topics?.length || 0})</p>
                        <div className="space-y-1 max-h-40 overflow-y-auto">
                          {blogSettings?.topics?.map((topic, idx) => (
                            <p key={idx} className="text-sm text-gray-600">• {topic}</p>
                          ))}
                        </div>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <p className="font-semibold mb-2">Keywords ({blogSettings?.target_keywords?.length || 0})</p>
                        <div className="space-y-1 max-h-40 overflow-y-auto">
                          {blogSettings?.target_keywords?.map((keyword, idx) => (
                            <p key={idx} className="text-sm text-gray-600">• {keyword}</p>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Blog Posts List */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="text-blue-600" />
                    Published Blog Posts ({blogPosts.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {blogPosts.length === 0 ? (
                    <p className="text-center text-gray-600 py-8">No blog posts yet. Generate your first post above!</p>
                  ) : (
                    <div className="space-y-4">
                      {blogPosts.map(post => (
                        <div key={post.slug} className="border-2 border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-semibold text-gray-900">{post.title}</h4>
                                {post.auto_generated && (
                                  <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded">
                                    🤖 Auto
                                  </span>
                                )}
                                <span className={`text-xs px-2 py-1 rounded ${
                                  post.status === 'published' ? 'bg-green-100 text-green-700' :
                                  post.status === 'draft' ? 'bg-gray-100 text-gray-700' :
                                  'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {post.status?.toUpperCase()}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 mb-2">{post.excerpt}</p>
                              <div className="flex gap-4 text-xs text-gray-500">
                                <span>👁️ {post.view_count} views</span>
                                <span>📅 {new Date(post.published_at).toLocaleDateString()}</span>
                                <span>🎯 {post.target_keyword}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2 mt-3">
                            <a
                              href={`/blog/${post.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                              👁️ View
                            </a>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingBlog(post);
                                setBlogEditForm({
                                  title: post.title,
                                  content: post.content,
                                  excerpt: post.excerpt,
                                  status: post.status
                                });
                              }}
                            >
                              ✏️ Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={async () => {
                                if (window.confirm('Delete this blog post?')) {
                                  try {
                                    await api.request(`/blog/admin/posts/${post.slug}`, { method: 'DELETE' });
                                    alert('✅ Blog post deleted!');
                                    loadDashboardData();
                                  } catch (err) {
                                    alert('Failed to delete: ' + err.message);
                                  }
                                }
                              }}
                            >
                              🗑️ Delete
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Edit Blog Modal */}
              {editingBlog && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="p-6">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-2xl font-bold">Edit Blog Post</h3>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingBlog(null)}
                        >
                          ✕ Close
                        </Button>
                      </div>

                      <div className="space-y-4">
                        {/* Title */}
                        <div>
                          <label className="block text-sm font-semibold mb-2">Title</label>
                          <input
                            type="text"
                            value={blogEditForm.title}
                            onChange={(e) => setBlogEditForm({ ...blogEditForm, title: e.target.value })}
                            className="w-full p-3 border rounded-lg"
                            placeholder="Blog post title..."
                          />
                        </div>

                        {/* Excerpt */}
                        <div>
                          <label className="block text-sm font-semibold mb-2">Excerpt (Short Summary)</label>
                          <textarea
                            value={blogEditForm.excerpt}
                            onChange={(e) => setBlogEditForm({ ...blogEditForm, excerpt: e.target.value })}
                            className="w-full p-3 border rounded-lg"
                            rows="3"
                            placeholder="Brief summary of the blog post..."
                          />
                        </div>

                        {/* Content */}
                        <div>
                          <label className="block text-sm font-semibold mb-2">Content (Markdown supported)</label>
                          <textarea
                            value={blogEditForm.content}
                            onChange={(e) => setBlogEditForm({ ...blogEditForm, content: e.target.value })}
                            className="w-full p-3 border rounded-lg font-mono text-sm"
                            rows="15"
                            placeholder="Blog post content..."
                          />
                        </div>

                        {/* Status */}
                        <div>
                          <label className="block text-sm font-semibold mb-2">Status</label>
                          <select
                            value={blogEditForm.status}
                            onChange={(e) => setBlogEditForm({ ...blogEditForm, status: e.target.value })}
                            className="w-full p-3 border rounded-lg"
                          >
                            <option value="published">Published</option>
                            <option value="draft">Draft</option>
                          </select>
                        </div>

                        {/* Save Button */}
                        <div className="flex gap-3 pt-4">
                          <Button
                            onClick={async () => {
                              try {
                                await api.request(`/blog/admin/posts/${editingBlog.slug}`, {
                                  method: 'PUT',
                                  body: JSON.stringify(blogEditForm)
                                });
                                alert('✅ Blog post updated successfully!');
                                setEditingBlog(null);
                                loadDashboardData();
                              } catch (err) {
                                alert('❌ Failed to update: ' + err.message);
                              }
                            }}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            💾 Save Changes
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => setEditingBlog(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Keywords Tab */}
          <TabsContent value="keywords">
            <div className="space-y-6">
              {/* Keyword Stats - Now Clickable */}
              <div className="grid md:grid-cols-4 gap-4">
                <Card 
                  className={`cursor-pointer transition-all hover:shadow-lg ${keywordFilter === 'all' ? 'ring-2 ring-purple-600' : ''}`}
                  onClick={() => setKeywordFilter('all')}
                >
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-purple-600">{keywordStats?.total || 0}</div>
                    <div className="text-sm text-gray-600">Total Keywords</div>
                    {keywordFilter === 'all' && <div className="text-xs text-purple-600 mt-1">✓ Viewing All</div>}
                  </CardContent>
                </Card>
                <Card 
                  className={`cursor-pointer transition-all hover:shadow-lg ${keywordFilter === 'high_relevance' ? 'ring-2 ring-green-600' : ''}`}
                  onClick={() => setKeywordFilter('high_relevance')}
                >
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-green-600">{keywordStats?.high_relevance || 0}</div>
                    <div className="text-sm text-gray-600">High Relevance (8+)</div>
                    {keywordFilter === 'high_relevance' && <div className="text-xs text-green-600 mt-1">✓ Filtered</div>}
                  </CardContent>
                </Card>
                <Card 
                  className={`cursor-pointer transition-all hover:shadow-lg ${keywordFilter === 'easy' ? 'ring-2 ring-blue-600' : ''}`}
                  onClick={() => setKeywordFilter('easy')}
                >
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-blue-600">
                      {keywordStats?.by_difficulty?.easy || 0}
                    </div>
                    <div className="text-sm text-gray-600">Easy to Rank</div>
                    {keywordFilter === 'easy' && <div className="text-xs text-blue-600 mt-1">✓ Filtered</div>}
                  </CardContent>
                </Card>
                <Card 
                  className={`cursor-pointer transition-all hover:shadow-lg ${keywordFilter === 'commercial' ? 'ring-2 ring-orange-600' : ''}`}
                  onClick={() => setKeywordFilter('commercial')}
                >
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-orange-600">
                      {keywordStats?.by_intent?.commercial || 0}
                    </div>
                    <div className="text-sm text-gray-600">Commercial Intent</div>
                    {keywordFilter === 'commercial' && <div className="text-xs text-orange-600 mt-1">✓ Filtered</div>}
                  </CardContent>
                </Card>
              </div>

              {/* Keyword Actions */}
              <Card>
                <CardHeader>
                  <CardTitle>Keyword Research Actions</CardTitle>
                  <p className="text-sm text-gray-600 mt-2">
                    Use AI to discover and analyze keywords for SEO strategy. Note: Each action takes 1-3 minutes to complete.
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Button
                        onClick={async () => {
                          if (!confirm('This will discover 15 trending keywords using AI. It may take 1-2 minutes. Continue?')) return;
                          setKeywordActionLoading('discover');
                          try {
                            await api.request('/keywords/discover-trending', { method: 'POST' });
                            alert('✅ Discovered 15 trending keywords! Check the list below.');
                            loadDashboardData();
                          } catch (err) {
                            alert('❌ Failed: ' + err.message);
                          } finally {
                            setKeywordActionLoading(null);
                          }
                        }}
                        className="bg-blue-600 w-full"
                        disabled={keywordActionLoading === 'discover'}
                      >
                        {keywordActionLoading === 'discover' ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin inline" />
                            Discovering...
                          </>
                        ) : (
                          '🔍 Discover Trending Keywords'
                        )}
                      </Button>
                      <p className="text-xs text-gray-500 mt-2">
                        AI finds 15 relevant keywords based on current trends (~1-2 min)
                      </p>
                    </div>
                    <div>
                      <Button
                        onClick={async () => {
                          if (!confirm('This will analyze 30+ UPost feature keywords using AI. Takes 2-3 minutes. Continue?')) return;
                          setKeywordActionLoading('seed');
                          try {
                            const response = await api.request('/keywords/seed-initial-keywords', { method: 'POST' });
                            alert(`✅ Analyzed ${response.seeded || 30} feature keywords! Check the list below.`);
                            loadDashboardData();
                          } catch (err) {
                            alert('❌ Failed: ' + err.message);
                          } finally {
                            setKeywordActionLoading(null);
                          }
                        }}
                        variant="outline"
                        className="w-full"
                        disabled={keywordActionLoading === 'seed'}
                      >
                        {keywordActionLoading === 'seed' ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin inline" />
                            Analyzing 30+ keywords...
                          </>
                        ) : (
                          '🌱 Seed Feature Keywords'
                        )}
                      </Button>
                      <p className="text-xs text-gray-500 mt-2">
                        Analyzes 30+ UPost feature keywords (AI writing, novel, screenplay, etc.) (~2-3 min)
                      </p>
                    </div>
                    <div>
                      <Button
                        onClick={async () => {
                          const keyword = prompt('Enter a keyword to analyze with AI:');
                          if (!keyword) return;
                          setKeywordActionLoading('analyze');
                          try {
                            await api.request('/keywords/analyze', {
                              method: 'POST',
                              body: JSON.stringify({ keyword })
                            });
                            alert(`✅ Analyzed "${keyword}"! Check the list below.`);
                            loadDashboardData();
                          } catch (err) {
                            alert('❌ Failed: ' + err.message);
                          } finally {
                            setKeywordActionLoading(null);
                          }
                        }}
                        variant="outline"
                        className="w-full"
                        disabled={keywordActionLoading === 'analyze'}
                      >
                        {keywordActionLoading === 'analyze' ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin inline" />
                            Analyzing...
                          </>
                        ) : (
                          '➕ Analyze Custom Keyword'
                        )}
                      </Button>
                      <p className="text-xs text-gray-500 mt-2">
                        Enter any keyword for AI analysis (search volume, difficulty, variations) (~30 sec)
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Keywords List with Filtering */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>
                      Tracked Keywords 
                      {(() => {
                        const filtered = keywords.filter(kw => {
                          if (keywordFilter === 'high_relevance') return kw.relevance_score >= 8;
                          if (keywordFilter === 'easy') return kw.difficulty === 'easy';
                          if (keywordFilter === 'commercial') return kw.search_intent === 'commercial';
                          return true;
                        });
                        return ` (${filtered.length})`;
                      })()}
                    </span>
                    {keywordFilter !== 'all' && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setKeywordFilter('all')}
                      >
                        Clear Filter
                      </Button>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {keywords.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-600 mb-4">No keywords yet. Discover trending keywords to get started!</p>
                      <Button
                        onClick={async () => {
                          setKeywordActionLoading('discover');
                          try {
                            await api.request('/keywords/discover-trending', { method: 'POST' });
                            loadDashboardData();
                          } catch (err) {
                            alert('❌ Failed: ' + err.message);
                          } finally {
                            setKeywordActionLoading(null);
                          }
                        }}
                        className="bg-blue-600"
                        disabled={keywordActionLoading === 'discover'}
                      >
                        {keywordActionLoading === 'discover' ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin inline" />
                            Discovering...
                          </>
                        ) : (
                          '🔍 Discover Now'
                        )}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[600px] overflow-y-auto">
                      {keywords
                        .filter(kw => {
                          if (keywordFilter === 'high_relevance') return kw.relevance_score >= 8;
                          if (keywordFilter === 'easy') return kw.difficulty === 'easy';
                          if (keywordFilter === 'commercial') return kw.search_intent === 'commercial';
                          return true;
                        })
                        .sort((a, b) => (b.relevance_score || 0) - (a.relevance_score || 0))
                        .slice(0, 50)
                        .map((kw, idx) => (
                        <div key={idx} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs text-gray-500 font-mono">#{idx + 1}</span>
                                <h4 className="font-semibold text-gray-900">{kw.keyword}</h4>
                                <span className={`text-xs px-2 py-1 rounded ${
                                  kw.relevance_score >= 8 ? 'bg-green-100 text-green-700' :
                                  kw.relevance_score >= 6 ? 'bg-blue-100 text-blue-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  Score: {kw.relevance_score}/10
                                </span>
                                <span className={`text-xs px-2 py-1 rounded ${
                                  kw.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                                  kw.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {kw.difficulty}
                                </span>
                                <span className="text-xs px-2 py-1 rounded bg-purple-100 text-purple-700">
                                  {kw.search_intent}
                                </span>
                              </div>
                              {kw.blog_topics && kw.blog_topics.length > 0 && (
                                <div className="text-sm text-gray-600 mb-2">
                                  <strong>Blog Ideas:</strong> {kw.blog_topics[0]}
                                </div>
                              )}
                              {kw.variations && kw.variations.length > 0 && (
                                <div className="text-xs text-gray-500">
                                  <strong>Variations:</strong> {kw.variations.slice(0, 3).join(', ')}...
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      {keywords.filter(kw => {
                        if (keywordFilter === 'high_relevance') return kw.relevance_score >= 8;
                        if (keywordFilter === 'easy') return kw.difficulty === 'easy';
                        if (keywordFilter === 'commercial') return kw.search_intent === 'commercial';
                        return true;
                      }).length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          No keywords match this filter. Try a different filter or discover more keywords.
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="rankings">
            <KeywordTracker />
          </TabsContent>

          <TabsContent value="offsiteseo">
            <OffSiteSEO />
          </TabsContent>

          <TabsContent value="voice-ai">
            <VoiceAISearch />
          </TabsContent>

          <TabsContent value="exchange">
            <ExchangeAdmin />
          </TabsContent>

          <TabsContent value="email-log">
            <EmailLogAdmin />
          </TabsContent>

          <TabsContent value="cleanup">
            <CleanupAdmin />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
